import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Plus, PencilSimple, Trash, Buildings, MapPin, CalendarBlank, X,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { LOT_STATUTS, LOT_STATUT_MAP, CORPS_ETAT_BTP } from '../../constants/lotStatuts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_LOT = {
  name: '', code: '', description: '', entreprises: [],
  address: '', statut: 'a_demarrer', start_date: '', end_date: '',
  corps_etat: '',
};

/**
 * LotsManagerSheet - Sprint 3
 * Sheet lateral qui liste, cree, edite, supprime les lots d'un chantier.
 */
export function LotsManagerSheet({ open, onOpenChange, chantier, onChange }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_LOT });
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [entrepriseInput, setEntrepriseInput] = useState('');

  const fetchLots = useCallback(async () => {
    if (!chantier?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chantiers/${chantier.id}/lots`, { withCredentials: true });
      setLots(res.data || []);
    } catch (_err) { toast.error('Erreur chargement lots'); }
    finally { setLoading(false); }
  }, [chantier?.id]);

  useEffect(() => { if (open && chantier?.id) fetchLots(); }, [open, chantier?.id, fetchLots]);

  const openCreate = () => {
    setEditingLot(null);
    setFormData({ ...EMPTY_LOT });
    setShowForm(true);
  };

  const openEdit = (lot) => {
    setEditingLot(lot);
    setFormData({
      name: lot.name || '', code: lot.code || '', description: lot.description || '',
      entreprises: lot.entreprises || [], address: lot.address || '',
      statut: lot.statut || 'a_demarrer',
      start_date: lot.start_date || '', end_date: lot.end_date || '',
      corps_etat: lot.corps_etat || '',
    });
    setShowForm(true);
  };

  const addEntreprise = () => {
    const v = entrepriseInput.trim();
    if (!v) return;
    if (formData.entreprises.includes(v)) return;
    setFormData({ ...formData, entreprises: [...formData.entreprises, v] });
    setEntrepriseInput('');
  };

  const removeEntreprise = (name) => {
    setFormData({ ...formData, entreprises: formData.entreprises.filter(e => e !== name) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Nom du lot requis'); return; }
    setSubmitting(true);
    try {
      if (editingLot) {
        await axios.put(`${API}/lots/${editingLot.id}`, formData, { withCredentials: true });
        toast.success('Lot mis a jour');
      } else {
        await axios.post(`${API}/chantiers/${chantier.id}/lots`, formData, { withCredentials: true });
        toast.success('Lot cree');
      }
      setShowForm(false);
      setEditingLot(null);
      await fetchLots();
      onChange?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur sauvegarde';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (lot) => {
    try {
      await axios.delete(`${API}/lots/${lot.id}`, { withCredentials: true });
      toast.success('Lot supprime');
      setDeleteConfirm(null);
      await fetchLots();
      onChange?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur suppression';
      toast.error(msg);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lots-manager-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2" data-testid="lots-manager-title">
              <Buildings size={22} className="text-[#FF6B00]" weight="fill" />
              Lots : {chantier?.name || '...'}
            </SheetTitle>
            <SheetDescription>
              Gerez les lots du chantier. Chaque lot peut avoir ses propres entreprises et adresse.
            </SheetDescription>
          </SheetHeader>

          {!showForm && (
            <>
              <div className="flex justify-between items-center mt-6 mb-3">
                <p className="text-sm text-gray-600">
                  {lots.length} lot{lots.length > 1 ? 's' : ''}
                </p>
                <Button onClick={openCreate}
                  className="bg-[#FF6B00] hover:bg-orange-600"
                  data-testid="new-lot-btn">
                  <Plus size={16} className="mr-2" weight="bold" />
                  Nouveau lot
                </Button>
              </div>

              {loading ? (
                <div className="py-10 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto" />
                </div>
              ) : lots.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-8">
                  Aucun lot. Cliquez sur "Nouveau lot" pour commencer.
                </p>
              ) : (
                <div className="space-y-3" data-testid="lots-list">
                  {lots.map(lot => {
                    const statutInfo = LOT_STATUT_MAP[lot.statut];
                    return (
                      <Card key={lot.id} className="border-l-4 border-l-[#FF6B00]"
                        data-testid={`lot-card-${lot.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {lot.code && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-bold">
                                    {lot.code}
                                  </span>
                                )}
                                <h3 className="font-semibold text-[#111827]">{lot.name}</h3>
                                {statutInfo && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statutInfo.color}`}
                                    data-testid={`lot-statut-${lot.id}`}>
                                    {statutInfo.label}
                                  </span>
                                )}
                                {lot.is_default && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 uppercase">
                                    Defaut
                                  </span>
                                )}
                              </div>
                              {lot.description && (
                                <p className="text-xs text-gray-500 mb-1">{lot.description}</p>
                              )}
                              {lot.corps_etat && (
                                <p className="text-xs text-gray-600 mb-1">
                                  Corps d'etat : <span className="font-medium">{lot.corps_etat}</span>
                                </p>
                              )}
                              {lot.address && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                                  <MapPin size={12} /> {lot.address}
                                </p>
                              )}
                              {(lot.start_date || lot.end_date) && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                                  <CalendarBlank size={12} />
                                  {lot.start_date || '?'} → {lot.end_date || '?'}
                                </p>
                              )}
                              {lot.entreprises?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {lot.entreprises.map((e, i) => (
                                    <span key={`${lot.id}-ent-${i}`}
                                      className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                      {e}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button onClick={() => openEdit(lot)}
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                                data-testid={`lot-edit-${lot.id}`}>
                                <PencilSimple size={16} />
                              </button>
                              <button onClick={() => setDeleteConfirm(lot)}
                                disabled={lots.length <= 1}
                                title={lots.length <= 1 ? 'Impossible de supprimer le dernier lot' : 'Supprimer'}
                                className="p-1.5 hover:bg-red-50 rounded text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`lot-delete-${lot.id}`}>
                                <Trash size={16} />
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="lot-form">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="lot-name">Nom *</Label>
                  <Input id="lot-name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Gros oeuvre"
                    className="h-11 mt-1" data-testid="lot-name" required />
                </div>
                <div>
                  <Label htmlFor="lot-code">Code</Label>
                  <Input id="lot-code" value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="L02"
                    className="h-11 mt-1" data-testid="lot-code" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="lot-statut">Statut</Label>
                  <Select value={formData.statut}
                    onValueChange={(v) => setFormData({ ...formData, statut: v })}>
                    <SelectTrigger id="lot-statut" className="h-11 mt-1" data-testid="lot-statut-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[100]">
                      {LOT_STATUTS.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="lot-corps">Corps d'etat</Label>
                  <Select value={formData.corps_etat}
                    onValueChange={(v) => setFormData({ ...formData, corps_etat: v })}>
                    <SelectTrigger id="lot-corps" className="h-11 mt-1" data-testid="lot-corps-select">
                      <SelectValue placeholder="Optionnel" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[100]">
                      {CORPS_ETAT_BTP.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="lot-start">Date debut</Label>
                  <Input id="lot-start" type="date" value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="h-11 mt-1" data-testid="lot-start" />
                </div>
                <div>
                  <Label htmlFor="lot-end">Date fin</Label>
                  <Input id="lot-end" type="date" value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="h-11 mt-1" data-testid="lot-end" />
                </div>
              </div>

              <div>
                <Label htmlFor="lot-address">Adresse (surcharge optionnelle du chantier)</Label>
                <Input id="lot-address" value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Laisser vide pour heriter de l'adresse du chantier"
                  className="h-11 mt-1" data-testid="lot-address" />
              </div>

              <div>
                <Label htmlFor="lot-desc">Description</Label>
                <Textarea id="lot-desc" rows={2} value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1" data-testid="lot-description" />
              </div>

              <div>
                <Label>Entreprises intervenantes</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={entrepriseInput}
                    onChange={(e) => setEntrepriseInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntreprise(); } }}
                    placeholder="Nom d'entreprise + Entree"
                    className="h-11" data-testid="lot-entreprise-input" />
                  <Button type="button" onClick={addEntreprise} variant="outline"
                    className="h-11" data-testid="lot-entreprise-add">
                    <Plus size={16} />
                  </Button>
                </div>
                {formData.entreprises.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.entreprises.map((e, i) => (
                      <span key={`form-ent-${i}`}
                        className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {e}
                        <button type="button" onClick={() => removeEntreprise(e)}
                          className="text-gray-400 hover:text-red-500"
                          data-testid={`lot-entreprise-remove-${i}`}>
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline"
                  onClick={() => { setShowForm(false); setEditingLot(null); }}
                  data-testid="lot-form-cancel">
                  Annuler
                </Button>
                <Button type="submit" disabled={submitting}
                  className="bg-[#FF6B00] hover:bg-orange-600"
                  data-testid="lot-form-submit">
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingLot ? 'Enregistrer' : 'Creer le lot'}
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => { if (!v) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="lot-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce lot ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le lot "{deleteConfirm?.name}" sera supprime. Les entites liees (taches, inspections,
              reserves) seront detachees mais conservees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lot-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-red-500 hover:bg-red-600"
              data-testid="lot-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default LotsManagerSheet;
